import type { IndexingStatus } from '@vrzl/database';
import { VTooltip, VTooltipContent, VTooltipTrigger } from '@vrzl/ui';
import type { ReactNode } from 'react';

import { indexingStatusDescriptions, indexingStatusLabels } from '../constants';

function formatIndexingErrors(errors: unknown): string {
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors) && errors.every((e) => typeof e === 'string')) {
    return errors.join('\n');
  }
  return JSON.stringify(errors, null, 2);
}

type IndexingStatusTooltipProps = {
  status: IndexingStatus;
  errors?: unknown;
  children: ReactNode;
};

/**
 * Hover tooltip around an indexing status icon: a short explanation per
 * status, plus the raw error details when indexing failed.
 */
export function IndexingStatusTooltip({
  status,
  errors,
  children,
}: IndexingStatusTooltipProps) {
  const showErrors = status === 'FAILED_WITH_ERRORS' && errors != null;

  return (
    <VTooltip>
      <VTooltipTrigger asChild>
        <span className="inline-flex items-center cursor-help">{children}</span>
      </VTooltipTrigger>
      <VTooltipContent className="max-w-sm">
        <div className="font-medium">{indexingStatusLabels[status]}</div>
        <div className="mt-1 text-muted-foreground">
          {indexingStatusDescriptions[status]}
        </div>
        {showErrors && (
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">
            {formatIndexingErrors(errors)}
          </pre>
        )}
      </VTooltipContent>
    </VTooltip>
  );
}
